/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  HttpRequest,
  HttpHandler,
  HttpEvent,
  HttpResponse,
  HttpInterceptor,
  HttpErrorResponse,
} from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Observable } from 'rxjs';
import { delay, finalize, tap } from 'rxjs/operators';
import { NgxUiLoaderService } from 'ngx-ui-loader';
import { ToasterService } from '../../services/toaster.service';
import { DialogServiceWrapper } from '../../services/dialog.service';
import { responseStatus } from '../constants/responseStatus.const';
import { SKIP_LOADER } from './skip-loader.token';
import { TranslateService } from '@ngx-translate/core';
import {
  resolveApiMessage,
  resolveHttpErrorByStatus,
} from '../utils/http-error.util';
import { safeLocalStorageClear } from '../utils/safe-storage.util';

@Injectable({
  providedIn: 'root',
})
export class GetInterceptor implements HttpInterceptor {
  private recentErrors = new Set<string>();
  private errorTimeout = 5000;
  constructor(
    private router: Router,
    private _toastrService: ToasterService,
    private ngxLoader: NgxUiLoaderService,
    private dialogWrapper: DialogServiceWrapper,
    private translate: TranslateService
  ) {}

  intercept(
    req: HttpRequest<any>,
    next: HttpHandler
  ): Observable<HttpEvent<any>> {
    const skipLoader = req.context.get(SKIP_LOADER);
    if (!skipLoader) {
      this.ngxLoader.start();
    }
    return next.handle(req).pipe(
      delay(0),
      tap({
        next: (event: HttpEvent<any>) => {
          if (event instanceof HttpResponse) {
            this.handleResponse(event.body, req.method);
          }
        },
        error: (error: HttpErrorResponse) => {
          this.handleError(error);
        },
      }),
      finalize(() => {
        if (!skipLoader) {
          this.ngxLoader.stop();
        }
      })
    );
  }

  private handleResponse(body: any, method: string): void {
    if (body?.statusCode) {
      switch (body.statusCode) {
        case responseStatus.SUCCESS:
          if (
            method === 'POST' ||
            method === 'PUT' ||
            method === 'DELETE' ||
            method === 'PATCH'
          ) {
            const successMessage = resolveApiMessage(body?.message);
            if (successMessage) {
              this._toastrService.showSuccess(successMessage);
            }
          }
          break;
        case responseStatus.BADREQUEST:
        case responseStatus.UNAUTHORIZED:
        case responseStatus.FORBIDDEN:
        case responseStatus.REQUESTTIMEOUT:
        case responseStatus.INTERNALSERVERERROR:
          this._toastrService.showError(
            this.resolveDisplayMessage(body?.message)
          );
          if (
            body.statusCode === responseStatus.UNAUTHORIZED ||
            body.statusCode === responseStatus.FORBIDDEN
          ) {
            this.dialogWrapper.closeDialog();
          }
          break;
        default:
          break;
      }
    }
  }

  private handleError(error: HttpErrorResponse): void {
    // Keep full technical details available for debugging without exposing
    // raw backend errors to the user.
    console.error('[HTTP Error]', {
      status: error.status,
      url: error.url,
      message: error.message,
      error: error.error,
    });

    const { translationKey, severity } = resolveHttpErrorByStatus(error.status);
    // Prefer the message sent by the API. Fall back to our status-based
    // translation only when the API omits a message (or sends an empty one).
    const apiMessage = resolveApiMessage(error.error?.message);
    const errorMessage = apiMessage || this.translate.instant(translationKey);

    if (!this.recentErrors.has(errorMessage)) {
      this.recentErrors.add(errorMessage);
      if (severity === 'warn') {
        this._toastrService.showWarning(errorMessage);
      } else {
        this._toastrService.showError(errorMessage);
      }
      setTimeout(
        () => this.recentErrors.delete(errorMessage),
        this.errorTimeout
      );
    }

    if (
      error.status === responseStatus.UNAUTHORIZED ||
      error.status === responseStatus.FORBIDDEN
    ) {
      this.dialogWrapper.closeDialog();
      this.router.navigate(['/auth/login']);
      safeLocalStorageClear();
    }
  }

  private resolveDisplayMessage(message?: string | null): string {
    const apiMessage = resolveApiMessage(message);

    if (apiMessage === 'SomethingWentWrong') {
      return this.translate.instant('SomethingWentWrong');
    }

    if (apiMessage) {
      return apiMessage;
    }

    return this.translate.instant('serverUnavailableTryAgain');
  }
}
